import React, {
  useState,
  useEffect,
  forwardRef,
  useImperativeHandle,
} from "react";
import StepperPage from "./StepperPage";
import Typography from "@material-ui/core/Typography";
import TextField from "@material-ui/core/TextField";
import BasicSharepointSettings from "../settings/basic/BasicSharepointSettings";
import BasicDriveSettings from "../settings/basic/BasicDriveSettings";
import BasicOneDriveSettings from "../settings/basic/BasicOneDriveSettings";
import BasicDropboxSettings from "../settings/basic/BasicDropboxSettings";
import BasicExpertSettings from "../settings/basic/BasicExpertSettings";
import { makeStyles } from "@material-ui/core/styles";
import InputAdornment from "@material-ui/core/InputAdornment";
import Visibility from "@material-ui/icons/Visibility";
import VisibilityOff from "@material-ui/icons/VisibilityOff";
import IconButton from "@material-ui/core/IconButton";
import FormControl from "@material-ui/core/FormControl";
import FormHelperText from "@material-ui/core/FormHelperText";
import MenuItem from "@material-ui/core/MenuItem";
import InputLabel from "@material-ui/core/InputLabel";
import Select from "@material-ui/core/Select";
import { Trans, useTranslation } from "react-i18next";
import TaxonomySelector from "../../../components/taxonomy/TaxonomySelector";
import Checkbox from "@material-ui/core/Checkbox";
import FormControlLabel from "@material-ui/core/FormControlLabel";
import Skeleton from "@material-ui/lab/Skeleton";
import DocumentType from "../../../components/DocumentType";
import DocumentCategories from "../../../components/document/DocumentCategories";
import { useSelector } from "react-redux";

const useStyles = makeStyles({
  container: {
    display: "flex",
    flexDirection: "column",
  },
  subContainer: {
    marginTop: 20,
  },
  spiderDepth: {
    marginRight: 20,
  },
  description: {
    width: "50%",
  },
  widthInput: {
    maxWidth: "20%",
  },
  classification: {
    width: "15%",
    marginBottom: 5,
  },
  honourRobots: {
    marginLeft: 20,
  },
});

function BasicSettings(props, ref) {
  const [username, setUsername] = useState("");
  const [password, setPassword] = useState("");
  const [description, setDescription] = useState("");
  const [descriptionTouched, setDescriptionTouched] = useState(false);
  const [topicTouched, setTopicTouched] = useState(false);
  const [
    childPresentationProperties,
    setChildPresentationProperties,
  ] = useState({});
  const [childReaderProperties, setChildReaderProperties] = useState({});
  const [childReindexerProperties, setChildReindexerProperties] = useState({});
  const [childSourceProperties, setChildSourceProperties] = useState({});
  const [childSpiderProperties, setChildSpiderProperties] = useState({});
  const [showPassword, setShowPassword] = useState(false);
  const [topic, setTopic] = useState(props.topic);
  const [topicName, setTopicName] = useState(props.topicName);
  const [classification, setClassification] = useState(0);
  const [spiderDepth, setSpiderDepth] = useState(0);
  const [spiderDelay, setSpiderDelay] = useState(0);
  const [documentType, setDocumentType] = useState(-1);
  const [categories, setCategories] = useState([]);
  const [honourRobots, setHonourRobots] = useState(false);
  const [sourceUrl, setSourceUrl] = useState(null);
  const { t } = useTranslation();
  const classes = useStyles();
  const documentTypes = useSelector((state) => state.prefs.documentTypes);

  useEffect(
    () => {
      if (props.sourceType === "GOOGLE_DRIVE") {
        setSpiderDelay(3);
      }
    },
    [props.sourceType]
  );

  useEffect(
    () => {
      if (description && topic) {
        if (
          props.sourceType !== "WEBSITE" &&
          props.sourceType !== "SHAREPOINT" && props.sourceType !== "ONEDRIVE"
        ) {
          props.setNextDisabled(false);
        } else {
          if (sourceUrl) {
            props.setNextDisabled(false);
          } else {
            props.setNextDisabled(true);
          }
        }
      } else {
        props.setNextDisabled(true);
      }
    },
    [description, sourceUrl, topic]
  );

  useEffect(
    () => {
      settingsUpdated("", "", false);
      setTopic(props.topic);
      setTopicName(props.topicName);
      if (props.editKs && Object.keys(props.editKs).length > 0) {
        setDescription(props.editKs.description);
        props.updateDescription(props.editKs.description);
        setTopic(props.editKs.topicId);
        props.updateTopic(props.editKs.topicId);
        setTopicName(props.editKs.topicName);
        setClassification(props.editKs.classification);
        props.updateClassification(props.editKs.classification);
        setSourceUrl(props.editKs.url);
        props.updateURL(props.editKs.url);
        setSpiderDepth(props.editKs.properties.spiderProperties.depthLimit);
        setSpiderDelay(props.editKs.properties.spiderProperties.spiderDelay);
        setHonourRobots(
          !!props.editKs.properties.spiderProperties.honourRobots
        );
        setUsername(props.editKs.properties.spiderProperties.authUser);
        setPassword(props.editKs.properties.spiderProperties.authPassword);
        setDocumentType(
          props.editKs.properties.sourceProperties.defaultDocumentType
        );
      }
    },
    [props.editKs]
  );

  useEffect(
    () => {
      settingsUpdated();
    },
    [
      username,
      password,
      spiderDepth,
      spiderDelay,
      honourRobots,
      documentType,
      categories,
      childPresentationProperties,
      childReaderProperties,
      childReindexerProperties,
      childSpiderProperties,
      childSourceProperties,
    ]
  );

  const showURLAndAuth = () => {
    return props.sourceType === "WEBSITE" || props.sourceType === "FILE_SHARE";
  };

  const settingsUpdated = () => {
    props.setSpiderProperties({
      ...childSpiderProperties,
      depthLimit: spiderDepth,
      spiderDelay,
      authUser: username,
      authPassword: password,
      honourRobots,
    });
    props.setSourceProperties({
      ...childSourceProperties,
      defaultDocumentType: documentType,
      defaultDocumentCategories: categories.map((c) => c.id),
    });
  };

  const onTopicSelected = (e) => {
    setTopicTouched(true);
    setTopic(e.id);
    setTopicName(e.name);
    props.updateTopic(e.id);
  };

  const onDescriptionChange = (e) => {
    setDescriptionTouched(true);
    setDescription(e.target.value);
    props.updateDescription(e.target.value);
  };

  const onSourceUrlChange = (e) => {
    setSourceUrl(e.target.value);
    props.updateURL(e.target.value);
  };

  const onSpiderDepthChange = (e) => {
    if (e.target.value < 0) {
      setSpiderDepth(0);
    } else {
      setSpiderDepth(e.target.value);
    }
  };

  const onSpiderDelayChange = (e) => {
    setSpiderDelay(e.target.value);
  };

  const onHonourRobotsChange = (e) => {
    setHonourRobots(e.target.checked);
  };

  const updateURL = (url) => {
    setSourceUrl(url);
    props.updateURL(url);
  };

  const setStateChildSpiderProperties = (spiderProperties) => {
    setChildSpiderProperties(spiderProperties);
  };

  const setStateChildSourceProperties = (sourceProperties) => {
    setChildSourceProperties(sourceProperties);
  };

  const setStateChildReaderProperties = (readerProperties) => {
    setChildReaderProperties(readerProperties);
  };

  return (
    <>
      <Typography variant="h5" gutterBottom>
        <Trans i18nKey="create_ks_basic_source_details" />
      </Typography>
      {props.editKs === true ? (
        <>
          <Skeleton
            variant="rect"
            style={{ width: "40%", marginBottom: 20 }}
            height={35}
            animation="wave"
          />
          <Skeleton
            variant="rect"
            style={{ width: "50%", marginBottom: 20 }}
            height={25}
            animation="wave"
          />
          <Skeleton
            variant="rect"
            style={{ width: "25%", marginBottom: 50 }}
            height={25}
            animation="wave"
          />
          <Skeleton
            variant="rect"
            style={{ width: "30%", marginBottom: 20 }}
            height={35}
            animation="wave"
          />
          <Skeleton
            variant="rect"
            style={{ width: "35%", marginBottom: 20 }}
            height={25}
            animation="wave"
          />
          <Skeleton
            variant="rect"
            style={{ width: "35%", marginBottom: 20 }}
            height={25}
            animation="wave"
          />
          <Skeleton
            variant="rect"
            style={{ width: "30%", marginBottom: 20 }}
            height={25}
            animation="wave"
          />
          <Skeleton
            variant="rect"
            style={{ width: "30%", marginBottom: 20 }}
            height={25}
            animation="wave"
          />
        </>
      ) : (
        <>
          <div className={classes.container}>
            <FormControl error={description === ""}>
              <TextField
                required
                value={description}
                onChange={(e) => onDescriptionChange(e)}
                onBlur={() => setDescriptionTouched(true)}
                label={t("create_ks_description_label")}
                id="ks-description"
                name="description"
                error={descriptionTouched && description === ""}
                className={classes.description}
              />
              {descriptionTouched &&
                description === "" && (
                  <FormHelperText>
                    <Trans i18nKey="create_ks_error_description_required" />
                  </FormHelperText>
                )}
            </FormControl>
            <div style={{ display: "flex" }}>
              <div style={{ marginRight: 20, marginTop: 16 }}>
                <TaxonomySelector
                  required
                  disabled={props.editKs}
                  onTouch={() => setTopicTouched(true)}
                  error={topicTouched && !topic}
                  onNodeSelected={onTopicSelected}
                  allowedSelections={["topic"]}
                  inputLabel={t("create_ks_pick_topic")}
                  filter={
                    topic ? [{ id: topic, type: "topic", name: topicName }] : []
                  }
                />
                <FormHelperText error={topicTouched && !topic}>
                  <Trans i18nKey="global_required" />
                </FormHelperText>
              </div>
              <div style={{ flex: 1 }}>
                <FormControl className={classes.classification} margin="normal">
                  <InputLabel id="ks-classification-label">
                    Classification
                  </InputLabel>
                  <Select
                    labelId="ks-classification-label"
                    id="ks-classification"
                    disabled={props.editKs}
                    onClick={(e) => {
                      if (!e.target.value) {
                        setClassification(0);
                        props.updateClassification(0);
                      } else {
                        setClassification(e.target.value);
                        props.updateClassification(e.target.value);
                      }
                    }}
                    value={classification}
                  >
                    <MenuItem value={0}>Public</MenuItem>
                    <MenuItem value={1}>Level 1</MenuItem>
                    <MenuItem value={2}>Level 2</MenuItem>
                    <MenuItem value={3}>Level 3</MenuItem>
                    <MenuItem value={4}>Level 4</MenuItem>
                    <MenuItem value={5}>Level 5</MenuItem>
                  </Select>
                </FormControl>
              </div>
            </div>
            {showURLAndAuth() && (
              <FormControl error={sourceUrl === ""}>
                <TextField
                  id="ks-source-url"
                  value={sourceUrl}
                  required
                  onChange={(e) => onSourceUrlChange(e)}
                  className={classes.description}
                  label={t("create_ks_source_url_label")}
                  name="sourceUrl"
                />
                {sourceUrl === "" && (
                  <FormHelperText>
                    <Trans i18nKey="create_ks_error_source_url_required" />
                  </FormHelperText>
                )}
              </FormControl>
            )}
            {props.sourceType !== "EXPERT" && (
              <div className={classes.subContainer}>
                <FormControl error={spiderDepth < 0 || spiderDepth === ""}>
                  <TextField
                    id="ks-spider-depth"
                    error={spiderDepth < 0 || spiderDepth === ""}
                    label={t("create_ks_depth_to_spider_label")}
                    name="spiderDepth"
                    inputProps={{ min: "0" }}
                    value={spiderDepth}
                    onChange={(e) => onSpiderDepthChange(e)}
                    className={classes.spiderDepth}
                    type="number"
                  />
                  {spiderDepth === "" && (
                    <FormHelperText>
                      <Trans i18nKey="create_ks_error_depth_to_spider_required" />
                    </FormHelperText>
                  )}
                </FormControl>
                <FormControl error={!spiderDelay || spiderDelay < 0}>
                  <TextField
                    id="ks-spider-delay"
                    label={t("create_ks_spider_delay_label")}
                    name="spiderDelay"
                    inputProps={{ min: "0" }}
                    type="number"
                    value={spiderDelay}
                    onChange={(e) => onSpiderDelayChange(e)}
                    helperText={
                      props.sourceType === "GOOGLE_DRIVE"
                        ? t("create_ks_google_drive_default_delay")
                        : ""
                    }
                  />
                  {spiderDelay === null && (
                    <FormHelperText>
                      <Trans i18nKey="create_ks_error_spider_delay_required" />
                    </FormHelperText>
                  )}
                  {spiderDelay < 0 && (
                    <FormHelperText>
                      <Trans i18nKey="create_ks_error_spider_delay_too_low" />
                    </FormHelperText>
                  )}
                </FormControl>
                {props.sourceType === "WEBSITE" && (
                  <FormControl className={classes.honourRobots}>
                    <FormControlLabel
                      control={
                        <Checkbox
                          id="ks-honour-robots"
                          name="honourRobots"
                          checked={honourRobots}
                          onChange={(e) => onHonourRobotsChange(e)}
                        />
                      }
                      label="Honour Robots"
                    />
                  </FormControl>
                )}
              </div>
            )}
            <div style={{ marginTop: 10, display: "flex", maxWidth: "50%" }}>
              <div style={{ flex: 1, marginRight: 10 }}>
                <DocumentType
                  id="basic-settings-document-type"
                  doctypes={documentTypes}
                  docTypeSelected={(e) => {
                    setDocumentType(e.target.value);
                  }}
                  documentType={[documentType]}
                />
                <FormHelperText>
                  <Trans i18nKey="create_ks_document_type_helper_text" />
                </FormHelperText>
              </div>
              <DocumentCategories
                onChange={setCategories}
                style={{ flex: 5 }}
                defaultSelectedCategories={
                  props.editKs &&
                  props.editKs.properties.sourceProperties
                    .defaultDocumentCategories
                }
              />
            </div>
          </div>
          <div>
            {props.sourceType === "SHAREPOINT" && (
              <BasicSharepointSettings
                editKs={props.editKs}
                updateURL={updateURL}
                setSpiderProperties={setStateChildSpiderProperties}
                setSourceProperties={setStateChildSourceProperties}
                setReaderProperties={setStateChildReaderProperties}
              />
            )}
            {props.sourceType === "ONEDRIVE" && (
              <BasicOneDriveSettings
                editKs={props.editKs}
                updateURL={updateURL}
                setSpiderProperties={setStateChildSpiderProperties}
                setSourceProperties={setStateChildSourceProperties}
                setReaderProperties={setStateChildReaderProperties}
              />
            )}
            {props.sourceType === "GOOGLE_DRIVE" && (
              <BasicDriveSettings
                editKs={props.editKs}
                setSpiderProperties={setStateChildSpiderProperties}
                setSourceProperties={setStateChildSourceProperties}
                setReaderProperties={setStateChildReaderProperties}
              />
            )}
            {props.sourceType === "DROPBOX" && (
              <BasicDropboxSettings
                editKs={props.editKs}
                setSpiderProperties={setStateChildSpiderProperties}
                setSourceProperties={setStateChildSourceProperties}
                setReaderProperties={setStateChildReaderProperties}
              />
            )}
            {props.sourceType === "EXPERT" && (
              <BasicExpertSettings
                editKs={props.editKs}
                setSpiderProperties={setStateChildSpiderProperties}
                setSourceProperties={setStateChildSourceProperties}
                setReaderProperties={setStateChildReaderProperties}
              />
            )}
          </div>
          <div className={classes.container}>
            {showURLAndAuth() && (
              <>
                <Typography variant="h5" style={{ marginTop: 20 }}>
                  <Trans i18nKey="create_ks_authentication_header" />
                </Typography>
                <TextField
                  style={{ marginRight: 10 }}
                  id="ks-auth-username"
                  className={classes.widthInput}
                  label={t("label_username")}
                  onChange={(e) => setUsername(e.target.value)}
                  value={username}
                />
                <TextField
                  label={t("label_password")}
                  id="ks-auth-password"
                  className={classes.widthInput}
                  onChange={(e) => setPassword(e.target.value)}
                  value={password}
                  type={showPassword ? "text" : "password"}
                  InputProps={{
                    endAdornment: (
                      <InputAdornment position="end">
                        <IconButton
                          aria-label="toggle password visibility"
                          onClick={() => setShowPassword(!showPassword)}
                          onMouseDown={(e) => e.preventDefault()}
                        >
                          {showPassword ? <Visibility /> : <VisibilityOff />}
                        </IconButton>
                      </InputAdornment>
                    ),
                  }}
                />
              </>
            )}
          </div>
        </>
      )}
    </>
  );
}

export default StepperPage(forwardRef(BasicSettings));
